import type { ReactNode } from "react";

import { cx } from "../lib/cx";
import type { CollaborationStatus } from "./session";

export interface BannerMessage {
  tone: "warning" | "danger";
  title: string;
  detail: string;
  /** A reload-required banner cannot be dismissed: reloading is the only remedy (§15). */
  dismissable: boolean;
}

/**
 * What to say about a session that is not simply connected. `connecting` says nothing at all —
 * a banner that flashes on every reconnect is noise, and reconnection is the normal case.
 */
export function bannerFor(
  status: CollaborationStatus,
  hasLocalPersistence = true,
): BannerMessage | null {
  switch (status) {
    case "reloadRequired":
      return {
        tone: "danger",
        title: "This editor is out of date",
        detail:
          "Reload the page to keep editing. Your document is still readable, but changes are " +
          "not being sent.",
        dismissable: false,
      };
    case "readOnly":
      return {
        tone: "warning",
        title: "You have read-only access",
        detail: "You can see live changes and leave comments, but not edit this document.",
        dismissable: true,
      };
    case "disconnected":
      return {
        tone: "warning",
        title: "Reconnecting",
        detail: hasLocalPersistence
          ? "Your changes are saved on this device and will sync when the connection returns."
          : "Editing is paused because this browser will not store changes offline.",
        dismissable: false,
      };
    case "connecting":
    case "connected":
      return null;
  }
}

export interface ConnectionBannerProps {
  status: CollaborationStatus;
  hasLocalPersistence?: boolean;
  className?: string;
}

export function ConnectionBanner({
  status,
  hasLocalPersistence = true,
  className,
}: ConnectionBannerProps): ReactNode {
  const message = bannerFor(status, hasLocalPersistence);
  if (message === null) {
    return null;
  }
  return (
    <div
      role="status"
      aria-live="polite"
      className={cx(
        "flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border px-3 py-2 text-sm",
        message.tone === "danger"
          ? "border-danger/40 bg-danger/10 text-danger"
          : "border-line bg-raised text-ink",
        className,
      )}
    >
      <span className="font-medium">{message.title}</span>
      <span className="text-muted">{message.detail}</span>
    </div>
  );
}
